"use client";

import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowLeft, Share2, User, Tag } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useParams } from "next/navigation";
import ReactMarkdown from "react-markdown";

import { ArticleSkeleton } from "@/components/ArticleSkeleton";

interface ArticleDetailClientProps {
  initialArticle: any;
}

export default function ArticleDetailPage({ initialArticle }: ArticleDetailClientProps) {
  const { slug } = useParams();
  const article = useQuery(api.community.getBySlug, { slug: slug as string }) || initialArticle;

  if (article === undefined) {
    return <ArticleSkeleton />;
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-4xl font-headline font-black mb-4">Story Not Found</h1>
        <p className="text-muted-foreground mb-8">The article you are looking for doesn't exist or has been moved.</p>
        <Button asChild rounded-full px-8>
          <Link href="/community">Back to Community</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />
      <main className="flex-grow pt-32 pb-24">
        <article className="container mx-auto px-6 max-w-4xl">
          {/* Back Button */}
          <Link href="/community" className="inline-flex items-center gap-2 text-primary font-black text-[10px] uppercase tracking-widest mb-12 hover:gap-4 transition-all">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Community</span>
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex flex-wrap gap-3 mb-8">
              <Badge className="bg-primary/10 text-primary px-4 py-1.5 rounded-full font-black text-[9px] uppercase tracking-widest border-none">
                {article.type}
              </Badge>
              {article.category && (
                <Badge className="bg-accent/10 text-accent px-4 py-1.5 rounded-full font-black text-[9px] uppercase tracking-widest border-none">
                  {article.category}
                </Badge>
              )}
            </div>

            <h1 className="font-headline text-4xl md:text-6xl font-black text-foreground mb-8 leading-[1.1] tracking-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-8 py-8 border-y border-border/50">
              <div className="flex items-center gap-3">
                {article.authorImage ? (
                  <div className="w-10 h-10 rounded-full overflow-hidden relative">
                    <Image
                      src={article.authorImage}
                      alt={article.authorName || 'Author'}
                      fill
                      className="object-cover"
                      style={{
                        transform: article.authorPhotoConfig
                          ? `scale(${article.authorPhotoConfig.scale}) translate(${article.authorPhotoConfig.x}%, ${article.authorPhotoConfig.y}%)`
                          : undefined
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center text-primary">
                    <User className="w-5 h-5" />
                  </div>
                )}
                <div>
                  <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Written By</p>
                  <p className="text-sm font-bold text-foreground">{article.authorName || article.author || 'Breeder'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center text-primary">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Published</p>
                  <p className="text-sm font-bold text-foreground">{new Date(article.publishedAt || article.createdAt).toLocaleDateString()}</p>
                </div>
              </div>

              <Button variant="ghost" size="icon" aria-label="Share story" className="ml-auto rounded-full hover:bg-secondary/50">
                <Share2 className="w-5 h-5" />
              </Button>
            </div>
          </header>

          {/* Featured Image */}
          {article.coverImage && (
            <div className="relative aspect-[21/9] rounded-[3rem] overflow-hidden shadow-luxury mb-16 border-4 border-card">
              <Image src={article.coverImage} alt={article.title} fill className="object-cover" priority />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none prose-headings:font-headline prose-headings:font-black prose-headings:tracking-tight prose-p:font-medium prose-p:text-muted-foreground prose-p:leading-relaxed">
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>

          {/* Author Bio Section */}
          {article.authorBio && (
            <div className="mt-16 p-8 bg-card border border-border/50 rounded-[2rem] flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left shadow-sm">
              {article.authorImage ? (
                <div className="w-20 h-20 rounded-full overflow-hidden relative flex-shrink-0 border-2 border-card shadow-lg">
                  <Image
                    src={article.authorImage}
                    alt={article.authorName || 'Author'}
                    fill
                    className="object-cover"
                    style={{
                      transform: article.authorPhotoConfig
                        ? `scale(${article.authorPhotoConfig.scale}) translate(${article.authorPhotoConfig.x}%, ${article.authorPhotoConfig.y}%)`
                        : undefined
                    }}
                  />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-full bg-secondary/50 flex items-center justify-center text-primary flex-shrink-0">
                  <User className="w-8 h-8" />
                </div>
              )}
              <div>
                <h2 className="font-headline font-bold text-lg text-foreground mb-2">About {article.authorName || 'the Author'}</h2>
                <p className="text-sm text-muted-foreground font-medium leading-relaxed">{article.authorBio}</p>
              </div>
            </div>
          )}

          {/* Footer CTA */}
          <div className="mt-24 p-12 bg-secondary/30 rounded-[4rem] text-center">
            <h2 className="font-headline text-3xl font-black text-foreground mb-4">Interested in a puppy?</h2>
            <p className="text-muted-foreground font-medium mb-8 max-w-md mx-auto">Reach out to learn more about our upcoming litters and program.</p>
            <Button asChild size="lg" className="rounded-full px-12 h-16 text-lg font-black shadow-luxury">
              <Link href="/contact">Inquire Now</Link>
            </Button>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
